import type { ReactNode } from 'react';

import { usePublicPageScroll } from '@/components/public/usePublicPageScroll';
import { cn } from '@/lib/utils';

/**
 * Shell for standalone public pages that center a card.
 *
 * The page scrolls with the DOCUMENT — `usePublicPageScroll` lifts the
 * dashboard's `height:100%` + `overflow:hidden` lock off html/body/#root for as
 * long as this is mounted. A nested `overflow-y-auto` scroller is deliberately
 * NOT used: the tip flow grows by ~500px when the embedded payment component
 * mounts, and only a document scroller gives the payer every way back to the
 * submit button (a real scrollbar that shows the page got longer, the wheel
 * anywhere on the page, Space / PageDown / End, and `scrollIntoView`).
 *
 * `min-h-dvh` (not `h-full`) keeps a short card centered while letting a tall
 * one grow past the viewport instead of being clipped — and `dvh` tracks the
 * mobile URL bar, so the last row stays reachable on a phone.
 *
 * `className` is for the page's own backdrop and inherited text styling
 * (background, font, text colour). Width constraints belong on the child card,
 * not here.
 */
export function PublicCenteredPage({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  usePublicPageScroll();

  return (
    <div
      className={cn(
        'flex min-h-dvh flex-col items-center justify-center bg-background px-4 py-8',
        className,
      )}
    >
      {children}
    </div>
  );
}
